import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ComplianceCase, EnforcementAction, EnforcementActionType, EnforcementCloseOutcome, EnforcementEvidenceKind, Obligation } from '@regigov/contracts';
import { enforcementApi, obligationsApi } from '../api/compliance';
import { useAuth } from '../auth/AuthContext';
import { Field, ProblemAlert, StatusBadge, formatDateTime } from './ui';

/** Today in Sydney time, the calendar the API checks conduct and decision dates against. */
const todayLocal = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

export const ENFORCEMENT_TYPES: Record<EnforcementActionType, string> = {
  formal_warning: 'Formal warning',
  infringement_notice: 'Infringement notice',
  enforceable_undertaking: 'Enforceable undertaking',
  compliance_direction: 'Compliance direction',
  suspension: 'Suspension',
};

export const CLOSE_OUTCOMES: Record<EnforcementCloseOutcome, string> = { complied: 'Complied', not_complied: 'Not complied' };

/** The server applies the same rule: `validFrom <= conduct date < validTo`. */
const inForceOn = (o: Obligation, date: string) => o.validFrom <= date && (o.validTo === null || o.validTo > date);

function useRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['case'] });
    void qc.invalidateQueries({ queryKey: ['cases'] });
    void qc.invalidateQueries({ queryKey: ['units'] });
    void qc.invalidateQueries({ queryKey: ['enforcement-actions'] });
  };
}

function Decision({ a }: { a: EnforcementAction }) {
  return (
    <>
      {a.decidedOn ? `Decided ${a.decidedOn}` : <span className="muted">Not yet decided</span>}
      {a.decisionMakerName && <div className="muted">Decision-maker: {a.decisionMakerName}</div>}
      {a.issuedAt && <div className="muted">Issued {formatDateTime(a.issuedAt)}</div>}
      {a.status === 'withdrawn' && (
        <div className="muted">
          Withdrawn{a.withdrawnAt ? ` ${formatDateTime(a.withdrawnAt)}` : ''}
          {a.withdrawnByName ? ` by ${a.withdrawnByName}` : ''}: {a.withdrawReason}
        </div>
      )}
      {a.status === 'closed' && a.closeOutcome && (
        <div className="muted">
          Closed ({CLOSE_OUTCOMES[a.closeOutcome]}){a.closedAt ? ` ${formatDateTime(a.closedAt)}` : ''}
          {a.closedByName ? ` by ${a.closedByName}` : ''}: {a.closeReason}
        </div>
      )}
    </>
  );
}

/** Enforcement actions on a case (C7): the list for everyone (participants see issued ones), plus CER drafting, issuing and withdrawing. */
export function EnforcementPanel({ c, mode }: { c: ComplianceCase; mode: 'cer' | 'participant' }) {
  const { user } = useAuth();
  const cer = mode === 'cer';
  const decisionMakers = useQuery({ queryKey: ['enforcement-actions', 'decision-makers'], queryFn: enforcementApi.decisionMakers, enabled: cer });
  const canDecide = Boolean(user && decisionMakers.data?.items.some((d) => d.id === user.id));
  const open = cer && c.status !== 'closed';
  return (
    <>
      <h2>Regulatory actions</h2>
      {c.enforcementActions.length === 0 ? (
        <p className="muted">No regulatory actions.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Action</th>
              <th>Conduct on</th>
              <th>Obligation relied on</th>
              <th>Evidence</th>
              <th>Reasons</th>
              <th>Status</th>
              <th>Decision</th>
            </tr>
          </thead>
          <tbody>
            {c.enforcementActions.map((a) => (
              <tr key={a.id}>
                <td>
                  {ENFORCEMENT_TYPES[a.type]}
                  <div>
                    <code>{a.id}</code>
                  </div>
                  {a.draftedByName && <div className="muted">Drafted by {a.draftedByName}</div>}
                </td>
                <td>{a.conductOn}</td>
                <td>
                  <code>
                    {a.obligation.code} v{a.obligation.version}
                  </code>
                  <div className="muted">
                    {a.obligation.title} (in force {a.obligation.validFrom} → {a.obligation.validTo ?? 'open'})
                  </div>
                </td>
                <td>
                  <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
                    {a.evidence.map((e) => (
                      <li key={`${e.kind}:${e.id}`}>{e.label}</li>
                    ))}
                  </ul>
                </td>
                <td style={{ whiteSpace: 'pre-wrap' }}>{a.reasons}</td>
                <td>
                  <StatusBadge status={a.status} />
                  {a.closeOutcome && <div className="muted">{CLOSE_OUTCOMES[a.closeOutcome]}</div>}
                </td>
                <td>
                  <Decision a={a} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ProblemAlert error={decisionMakers.error} />
      {open &&
        c.enforcementActions
          .filter((a) => a.status === 'draft' || (a.status === 'issued' && canDecide))
          .map((a) => <ActOnAction key={a.id} a={a} canIssue={a.status === 'draft' && a.decisionMakerUserId === user?.id} />)}
      {open && c.status === 'acting' && <RecordAction c={c} decisionMakers={decisionMakers.data?.items ?? []} />}
      {open && c.status !== 'acting' && <p className="muted">Move the case to acting to record a regulatory action.</p>}
    </>
  );
}

function ActOnAction({ a, canIssue }: { a: EnforcementAction; canIssue: boolean }) {
  const refresh = useRefresh();
  const [decidedOn, setDecidedOn] = useState(todayLocal());
  const [reason, setReason] = useState('');
  const issue = useMutation({ mutationFn: () => enforcementApi.issue(a.id, { decidedOn }), onSuccess: refresh });
  const withdraw = useMutation({ mutationFn: () => enforcementApi.withdraw(a.id, { reason }), onSuccess: () => (setReason(''), refresh()) });
  const [outcome, setOutcome] = useState<EnforcementCloseOutcome>('complied');
  const [closeReason, setCloseReason] = useState('');
  const close = useMutation({ mutationFn: () => enforcementApi.close(a.id, { outcome, reason: closeReason }), onSuccess: () => (setCloseReason(''), refresh()) });
  const busy = issue.isPending || withdraw.isPending || close.isPending;
  const drafted = a.createdAt.slice(0, 10);
  const earliest = a.conductOn > drafted ? a.conductOn : drafted;
  return (
    <div className="card">
      <h3>
        {ENFORCEMENT_TYPES[a.type]} <code>{a.id}</code> ({a.status})
      </h3>
      {a.status === 'draft' &&
        (canIssue ? (
          <form className="form" onSubmit={(e) => (e.preventDefault(), issue.mutate())}>
            <p className="muted">
              You are the decision-maker. Issuing records your decision under {a.obligation.code} v{a.obligation.version}, links the action to the case and shares it with {a.subjectParticipantName}.
            </p>
            <Field label="Decision date">
              <input type="date" value={decidedOn} min={earliest} max={todayLocal()} onChange={(e) => setDecidedOn(e.target.value)} required disabled={busy} />
            </Field>
            <ProblemAlert error={issue.error} />
            <div>
              <button type="submit" disabled={busy || !decidedOn}>
                Issue
              </button>
            </div>
          </form>
        ) : (
          <p className="muted">Awaiting a decision by {a.decisionMakerName ?? 'the named decision-maker'}; only they can issue it.</p>
        ))}
      {a.status === 'issued' && (
        <form className="form" onSubmit={(e) => (e.preventDefault(), close.mutate())}>
          <div className="row">
            <Field label="Close action: outcome" hint="Complied releases the units it put under enforcement to compliant; not complied leaves them non-compliant.">
              <select value={outcome} onChange={(e) => setOutcome(e.target.value as EnforcementCloseOutcome)} disabled={busy}>
                {Object.entries(CLOSE_OUTCOMES).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Reason" hint={`Recorded on the case timeline, visible to ${a.subjectParticipantName}.`}>
              <input value={closeReason} onChange={(e) => setCloseReason(e.target.value)} required disabled={busy} />
            </Field>
          </div>
          <ProblemAlert error={close.error} />
          <div>
            <button type="submit" disabled={busy || closeReason.trim().length < 3}>
              Close action
            </button>
          </div>
        </form>
      )}
      <form className="form" onSubmit={(e) => (e.preventDefault(), withdraw.mutate())}>
        <Field label={a.status === 'draft' ? 'Withdraw draft: reason' : 'Withdraw issued action: reason'} hint={a.status === 'issued' ? `Recorded on the case timeline, visible to ${a.subjectParticipantName}.` : 'CER only; the participant never sees drafts.'}>
          <input value={reason} onChange={(e) => setReason(e.target.value)} required disabled={busy} />
        </Field>
        <ProblemAlert error={withdraw.error} />
        <div>
          <button type="submit" disabled={busy || reason.trim().length < 3}>
            Withdraw
          </button>
        </div>
      </form>
    </div>
  );
}

type Option = { kind: EnforcementEvidenceKind; id: string; label: string };

/** Records on the case an action can rely on; the server checks each again. */
function evidenceOptions(c: ComplianceCase): Option[] {
  return [
    ...c.inspections.filter((i) => i.status === 'completed').map((i): Option => ({ kind: 'inspection', id: i.id, label: `Inspection ${i.id} (${i.outcome?.replace('_', '-')}, ${i.inspectedOn}): ${i.outcomeSummary ?? ''}` })),
    ...c.audits.flatMap((a) => a.findings.map((f): Option => ({ kind: 'audit_finding', id: String(f.id), label: `Audit ${a.id} finding ${f.seq} (${f.obligationRef}): ${f.finding}` }))),
    ...c.events.filter((e) => e.kind === 'evidence').map((e): Option => ({ kind: 'case_evidence', id: String(e.id), label: `Evidence #${e.id}: ${e.evidenceRef ?? ''}${e.note ? ` (${e.note})` : ''}` })),
  ];
}

function RecordAction({ c, decisionMakers }: { c: ComplianceCase; decisionMakers: Array<{ id: string; username: string; displayName: string }> }) {
  const refresh = useRefresh();
  const [type, setType] = useState<EnforcementActionType>('infringement_notice');
  const [conductOn, setConductOn] = useState('');
  const [code, setCode] = useState('');
  const [evidence, setEvidence] = useState<Set<string>>(new Set());
  const [decisionMaker, setDecisionMaker] = useState('');
  const [reasons, setReasons] = useState('');
  const obligations = useQuery({ queryKey: ['obligations'], queryFn: obligationsApi.list });
  const codes = [...new Set((obligations.data?.items ?? []).map((o) => o.code))];
  const versions = (obligations.data?.items ?? []).filter((o) => o.code === code).sort((a, b) => a.version - b.version);
  const applies = conductOn ? versions.find((o) => inForceOn(o, conductOn)) : undefined;
  const options = evidenceOptions(c);
  const key = (o: Option) => `${o.kind}:${o.id}`;
  const draft = useMutation({
    mutationFn: () =>
      enforcementApi.draft({
        caseId: c.id,
        type,
        conductOn,
        obligationCode: code,
        evidence: options.filter((o) => evidence.has(key(o))).map((o) => ({ kind: o.kind, id: o.id })),
        decisionMakerUserId: decisionMaker,
        reasons,
      }),
    onSuccess: () => (setEvidence(new Set()), setReasons(''), refresh()),
  });
  const busy = draft.isPending;
  return (
    <form className="form card" onSubmit={(e) => (e.preventDefault(), draft.mutate())}>
      <h2>Record regulatory action</h2>
      <p className="muted">Saved as a draft for the named decision-maker to issue. Drafts stay with CER; once issued the action is shared with {c.subjectParticipantName}.</p>
      <div className="row">
        <Field label="Type">
          <select value={type} onChange={(e) => setType(e.target.value as EnforcementActionType)} disabled={busy}>
            {Object.entries(ENFORCEMENT_TYPES).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Conduct date" hint="When the conduct the action answers took place.">
          <input type="date" value={conductOn} max={todayLocal()} onChange={(e) => setConductOn(e.target.value)} required disabled={busy} />
        </Field>
        <Field label="Obligation">
          <select value={code} onChange={(e) => setCode(e.target.value)} required disabled={busy}>
            <option value="">Select…</option>
            {codes.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <ProblemAlert error={obligations.error} />
      {code && (
        <fieldset disabled={busy}>
          <legend>Version relied on</legend>
          {!conductOn && <p className="muted">Enter the conduct date; the version in force on that date is selected.</p>}
          {versions.map((o) => {
            const inForce = conductOn !== '' && o === applies;
            return (
              <label key={o.id} className={inForce ? undefined : 'muted'} style={{ display: 'block' }}>
                <input type="radio" name="obligation-version" checked={inForce} disabled={!inForce} readOnly />{' '}
                {o.code} v{o.version} ({o.validFrom} → {o.validTo ?? 'open'}): {o.title}
                {conductOn && !inForce && <em> not in force at conduct date</em>}
              </label>
            );
          })}
          {conductOn && !applies && <p className="error">No version of {code} was in force on {conductOn}.</p>}
        </fieldset>
      )}
      <fieldset disabled={busy}>
        <legend>Evidence</legend>
        {options.length === 0 && <p className="muted">No completed inspections, audit findings or evidence entries on this case yet.</p>}
        {options.map((o) => (
          <label key={key(o)} style={{ display: 'block' }}>
            <input
              type="checkbox"
              checked={evidence.has(key(o))}
              onChange={(e) => {
                const next = new Set(evidence);
                if (e.target.checked) next.add(key(o));
                else next.delete(key(o));
                setEvidence(next);
              }}
            />{' '}
            {o.label}
          </label>
        ))}
      </fieldset>
      <Field label="Decision-maker" hint="The CER delegate who decides and issues the action.">
        <select value={decisionMaker} onChange={(e) => setDecisionMaker(e.target.value)} required disabled={busy}>
          <option value="">Choose…</option>
          {decisionMakers.map((d) => (
            <option key={d.id} value={d.id}>
              {d.displayName} ({d.username})
            </option>
          ))}
        </select>
      </Field>
      <Field label="Reasons" hint="Statement of reasons; shared with the participant once issued.">
        <textarea rows={4} value={reasons} onChange={(e) => setReasons(e.target.value)} required minLength={10} disabled={busy} />
      </Field>
      <ProblemAlert error={draft.error} />
      <div>
        <button type="submit" disabled={busy || !applies || evidence.size === 0 || !decisionMaker || reasons.trim().length < 10}>
          Save draft
        </button>
      </div>
    </form>
  );
}
